/**
 * Centralized hook for the current user's seller status.
 * Combines seller_limits_status + seller_credits_status into one cache.
 * Single source of truth — replaces ad-hoc fetchMySellerLimits / fetchMyCreditsStatus.
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export const CURRENT_SELLER_STATUS_KEY = 'current-seller-status';

export interface CurrentSellerStatus {
  // From seller_limits_status
  sellerTypeName: string | null;
  sellerTypeLabel: string | null;
  sellerTypeId: string | null;
  daysRemaining: number | null;
  sellerExpiresAt: string | null;
  sellerPlanStartedAt: string | null;
  hasMasterBadge: boolean;
  planPrice: number | null;
  creditsPerPlan: number | null;
  priorityBonus: number | null;
  averageRating: number | null;
  completedNegotiations: number | null;
  totalRatings: number | null;
  // From seller_credits_status
  totalCredits: number | null;
  usedCredits: number | null;
  availableCredits: number | null;
  isUnlimited: boolean;
  negotiationsPerYear: number | null;
  rechargeDiscountPercent: number | null;
  boostTotal: number | null;
  boostPlanTotal: number | null;
  boostPerformanceTotal: number | null;
  highestPlanTier: number | null;
  isVerified: boolean;
  verificationExpiresAt: string | null;
}

async function fetchCurrentSellerStatus(userId: string): Promise<CurrentSellerStatus | null> {
  const [limitsResult, creditsResult] = await Promise.all([
    supabase
      .from('seller_limits_status' as never)
      .select('seller_type_name, seller_type_label, seller_type_id, seller_expires_at, seller_plan_started_at, has_master_badge, plan_price, credits_per_plan, priority_bonus, average_rating, completed_negotiations, total_ratings')
      .eq('user_id', userId)
      .maybeSingle(),
    supabase
      .from('seller_credits_status' as any)
      .select('total_credits, used_credits, available_credits, is_unlimited, recharge_discount_percent, boost_total, boost_plan_total, boost_performance_total, highest_plan_tier, negotiations_per_year, is_verified, verification_expires_at')
      .eq('user_id', userId)
      .maybeSingle(),
  ]);

  const limits = limitsResult.data as any;
  const credits = creditsResult.data as any;

  if (!limits && !credits) return null;

  let daysRemaining: number | null = null;
  if (limits?.seller_expires_at) {
    const expiresAt = new Date(limits.seller_expires_at);
    const now = new Date();
    daysRemaining = Math.max(0, Math.ceil((expiresAt.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)));
  }

  return {
    sellerTypeName: limits?.seller_type_name || null,
    sellerTypeLabel: limits?.seller_type_label || null,
    sellerTypeId: limits?.seller_type_id || null,
    daysRemaining,
    sellerExpiresAt: limits?.seller_expires_at || null,
    sellerPlanStartedAt: limits?.seller_plan_started_at || null,
    hasMasterBadge: limits?.has_master_badge ?? false,
    planPrice: limits?.plan_price ?? null,
    creditsPerPlan: limits?.credits_per_plan ?? null,
    priorityBonus: limits?.priority_bonus ?? null,
    averageRating: limits?.average_rating ?? null,
    completedNegotiations: limits?.completed_negotiations ?? null,
    totalRatings: limits?.total_ratings ?? null,
    totalCredits: credits?.total_credits ?? null,
    usedCredits: credits?.used_credits ?? null,
    availableCredits: credits?.available_credits ?? null,
    isUnlimited: credits?.is_unlimited ?? false,
    negotiationsPerYear: credits?.negotiations_per_year ?? null,
    rechargeDiscountPercent: credits?.recharge_discount_percent ?? null,
    boostTotal: credits?.boost_total ?? null,
    boostPlanTotal: credits?.boost_plan_total ?? null,
    boostPerformanceTotal: credits?.boost_performance_total ?? null,
    highestPlanTier: credits?.highest_plan_tier ?? null,
    isVerified: credits?.is_verified ?? false,
    verificationExpiresAt: credits?.verification_expires_at ?? null,
  };
}

export function useCurrentSellerStatus(userId: string | undefined) {
  return useQuery({
    queryKey: [CURRENT_SELLER_STATUS_KEY, userId],
    queryFn: () => fetchCurrentSellerStatus(userId!),
    enabled: !!userId,
    staleTime: 5 * 60_000,
    gcTime: 15 * 60_000,
  });
}

/** Invalidate seller status cache after changes */
export function useInvalidateSellerStatus() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: [CURRENT_SELLER_STATUS_KEY] });
}
